import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { StatCard } from "@/components/common/StatCard";
import { SeverityBadge } from "@/components/common/SeverityBadge";
import { Skull, Bug, Server, Radar, Activity, ArrowRight, ShieldCheck, Target } from "lucide-react";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell } from "recharts";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";

const statusMap = {
  vulnerable: { c: "bg-primary/15 text-primary border-primary/40", label: "VULNÉRABLE" },
  clean: { c: "bg-success/15 text-success border-success/40", label: "CLEAN" },
};

const SEV_COLORS = {
  critical: "hsl(var(--destructive))",
  high: "hsl(var(--warning))",
  medium: "hsl(var(--info))",
  low: "hsl(var(--muted-foreground))",
  info: "hsl(var(--border))",
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [scans, setScans] = useState([]);
  const [vulns, setVulns] = useState([]);

  useEffect(() => {
    api.getStats().then(setStats).catch(() => {});
    api.listScans().then(setScans).catch(() => {});
    api.getVulnerabilities().then(setVulns).catch(() => {});
  }, []);

  const hostMap = new Map();
  scans.forEach(s => (s.hosts || []).forEach(h => {
    const prev = hostMap.get(h.ip);
    if (!prev) hostMap.set(h.ip, { ...h, target: s.target, scans: 1, lastScan: s.started_at });
    else prev.scans += 1;
  }));
  const hosts = [...hostMap.values()];
  const severityData = stats ? Object.entries(stats.severity)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: k, value: v, color: SEV_COLORS[k] })) : [];
  const hostBars = hosts.slice(0, 8).map(h => ({ name: h.ip, vulns: h.vulns, ports: h.open }));

  return (
    <div className="px-4 md:px-8 py-8 max-w-[1600px] mx-auto space-y-8 animate-fade-in" data-testid="dashboard-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="font-mono text-xs text-primary tracking-widest mb-2">// CRIMSON.OPS · DEFENSIVE POSTURE</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Command Center</h1>
          <p className="text-muted-foreground mt-1 text-sm">Vue consolidée de tes scans défensifs et des failles détectées.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/scanner"><Button className="bg-primary hover:bg-primary/90 text-primary-foreground font-mono text-xs gap-2"><Radar className="h-3.5 w-3.5"/>NOUVEAU SCAN</Button></Link>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Scans lancés" value={stats?.scans ?? 0} sub="opérations défensives" icon={Target} tone="info"/>
        <StatCard label="Hôtes analysés" value={stats?.hosts ?? 0} sub="services détectés" icon={Server}/>
        <StatCard label="Vulnérabilités" value={stats?.vulns ?? 0} sub="corrélées CVE / NSE" icon={Bug} tone="warning"/>
        <StatCard label="Critiques" value={stats?.critical ?? 0} sub="CVSS ≥ 9.0" icon={Skull} tone="danger"/>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 bg-card/60 border-border p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-mono text-[10px] tracking-widest text-muted-foreground mb-1">// PAR HÔTE</div>
              <h3 className="font-semibold">Failles & ports par hôte</h3>
            </div>
            <Badge variant="outline" className="font-mono text-[10px] border-success/40 text-success gap-1.5"><Activity className="h-3 w-3"/>LIVE</Badge>
          </div>
          {hostBars.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={hostBars}>
                <XAxis dataKey="name" tick={{fill:"hsl(var(--muted-foreground))", fontSize:10, fontFamily:"JetBrains Mono"}} axisLine={{stroke:"hsl(var(--border))"}} tickLine={false}/>
                <YAxis tick={{fill:"hsl(var(--muted-foreground))", fontSize:11, fontFamily:"JetBrains Mono"}} axisLine={false} tickLine={false} allowDecimals={false}/>
                <Tooltip contentStyle={{background:"hsl(var(--popover))", border:"1px solid hsl(var(--border))", borderRadius:6, fontFamily:"JetBrains Mono", fontSize:12}}/>
                <Bar dataKey="vulns" fill="hsl(var(--primary))" radius={[3,3,0,0]}/>
                <Bar dataKey="ports" fill="hsl(var(--info))" radius={[3,3,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="h-[240px] flex items-center justify-center text-muted-foreground font-mono text-sm">// aucun hôte scanné</div>}
        </Card>

        <Card className="bg-card/60 border-border p-5">
          <div className="mb-4">
            <div className="font-mono text-[10px] tracking-widest text-muted-foreground mb-1">// SÉVÉRITÉ</div>
            <h3 className="font-semibold">Répartition des failles</h3>
          </div>
          <div className="relative">
            {severityData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={severityData} innerRadius={55} outerRadius={85} paddingAngle={3} dataKey="value">
                    {severityData.map((s, i) => (<Cell key={i} fill={s.color} stroke="none"/>))}
                  </Pie>
                  <Tooltip contentStyle={{background:"hsl(var(--popover))", border:"1px solid hsl(var(--border))", borderRadius:6, fontFamily:"JetBrains Mono", fontSize:12}}/>
                </PieChart>
              </ResponsiveContainer>
            ) : <div className="h-[200px] flex items-center justify-center text-muted-foreground font-mono text-sm">// pas de données</div>}
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none" style={{height:200}}>
              {severityData.length > 0 && <>
                <div className="text-2xl font-bold font-mono text-primary">{stats?.vulns ?? 0}</div>
                <div className="font-mono text-[10px] tracking-widest text-muted-foreground">TOTAL</div>
              </>}
            </div>
          </div>
          <div className="mt-3 space-y-1.5">
            {severityData.map(s => (
              <div key={s.name} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2"><span className="h-2 w-2 rounded-full" style={{background:s.color}}/><span className="capitalize">{s.name}</span></div>
                <span className="font-mono text-muted-foreground">{s.value}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 bg-card/60 border-border overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <div>
              <div className="font-mono text-[10px] tracking-widest text-muted-foreground mb-1">// HÔTES</div>
              <h3 className="font-semibold">Hôtes scannés</h3>
            </div>
            <Link to="/vulnerabilities"><Button variant="ghost" size="sm" className="font-mono text-xs gap-1">Toutes les failles <ArrowRight className="h-3 w-3"/></Button></Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="font-mono text-[10px] tracking-widest text-muted-foreground bg-muted/30">
                  <th className="text-left px-5 py-2.5">IP</th>
                  <th className="text-left px-5 py-2.5 hidden lg:table-cell">OS</th>
                  <th className="text-left px-5 py-2.5">PORTS</th>
                  <th className="text-left px-5 py-2.5">VULNS</th>
                  <th className="text-left px-5 py-2.5 hidden md:table-cell">SCANS</th>
                  <th className="text-left px-5 py-2.5">STATUS</th>
                </tr>
              </thead>
              <tbody>
                {hosts.length === 0 && <tr><td colSpan={6} className="px-5 py-8 text-center text-muted-foreground font-mono text-sm">// aucun hôte — lance un scan</td></tr>}
                {hosts.map((h, i) => {
                  const st = h.vulns > 0 ? "vulnerable" : "clean";
                  return (
                    <tr key={`${h.ip}-${i}`} className="border-t border-border hover:bg-muted/20 transition-colors">
                      <td className="px-5 py-3 font-mono text-xs text-foreground">{h.ip}</td>
                      <td className="px-5 py-3 text-xs text-muted-foreground hidden lg:table-cell">{h.os || "—"}</td>
                      <td className="px-5 py-3 font-mono text-xs">{h.open}</td>
                      <td className="px-5 py-3 font-mono text-xs text-primary">{h.vulns}</td>
                      <td className="px-5 py-3 font-mono text-xs text-muted-foreground hidden md:table-cell">{h.scans}×</td>
                      <td className="px-5 py-3">
                        <span className={cn("inline-flex items-center gap-1.5 rounded px-2 py-0.5 text-[10px] font-mono font-semibold tracking-widest border", statusMap[st].c)}>
                          <span className="h-1.5 w-1.5 rounded-full bg-current"/>{statusMap[st].label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>

        <Card className="bg-card/60 border-border">
          <div className="px-5 py-4 border-b border-border">
            <div className="font-mono text-[10px] tracking-widest text-muted-foreground mb-1">// TOP FINDINGS</div>
            <h3 className="font-semibold">Failles les plus critiques</h3>
          </div>
          <div className="divide-y divide-border">
            {vulns.length === 0 && <div className="p-6 text-center text-muted-foreground font-mono text-sm">// aucune faille</div>}
            {vulns.slice(0, 5).map((v, i) => (
              <div key={`${v.id}-${i}`} className="p-4 hover:bg-muted/20 transition-colors">
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <div className="font-mono text-xs text-primary">{v.id}</div>
                  <SeverityBadge level={v.severity}/>
                </div>
                <div className="text-sm font-medium leading-snug">{v.title}</div>
                <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground font-mono">
                  <span>{v.host}:{v.port}</span>
                  <span className="text-warning">CVSS {Number(v.cvss || 0).toFixed(1)}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="p-3 border-t border-border">
            <Link to="/vulnerabilities"><Button variant="outline" className="w-full font-mono text-xs gap-2 border-border"><ShieldCheck className="h-3.5 w-3.5"/>VOIR & CORRIGER</Button></Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
